import {
  Alert,
  Button,
  Descriptions,
  Divider,
  Edit,
  Empty,
  Form,
  Icons,
  Modal,
  Select,
  Space,
  Table,
  TextField,
} from '@pankod/refine-antd'
import {
  CreateResponse,
  useCustomMutation,
  useInvalidate,
  useNavigation,
  useNotification,
  useOne,
  useResource,
} from '@pankod/refine-core'
import Head from 'next/head'
import React from 'react'
import dayjs from 'dayjs'
import { capitalize } from 'lodash'

import { TLMSProgramDetail } from '@resources/lms-management/program/types'
import { TCommonError, TCommonResponse } from 'src/interfaces/common'
import { PROGRAM_STATE } from '@resources/dasus-management/utils'
import { kapabilitasMapper } from '@components/DataTableParticipants/blocks/utils'
import { useWarnIfUnsavedChanges } from 'utils/hooks'
import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import UpdateParticipantsTable from '@resources/ops-toolkit/alter-program-participants/blocks/UpdateParticipantsTable'
import UpdateProgramLocalDrawer from '@resources/ops-toolkit/alter-program-participants/blocks/UpdateProgramLocalDrawer'

export interface ParticipantItem {
  userId: string
  email: string
  name: string
  kapabilitas: string
  participantType: string
}

export const AlterProgramParticipantsEdit = () => {
  const invalidate = useInvalidate()
  const { list, goBack } = useNavigation()
  const notification = useNotification()
  const [showProgramLocalDrawer, setShowProgramLocalDrawer] =
    React.useState(false)
  const [updatedParticipants, setUpdatedParticipants] = React.useState<
    Map<string, ParticipantItem>
  >(new Map())
  const [showModal, setShowModal] = React.useState<boolean>(false)
  const { mutateAsync: doSave, isLoading: isSaving } = useCustomMutation<
    {
      message: string
      failedParticipants: {
        userId: string
        name: string
        role: string
      }[]
    },
    TCommonError,
    any
  >()
  const [form] = Form.useForm()
  const { id } = useResource()

  const {
    data,
    isFetching: isProgramLoading,
    refetch: refetchProgram,
  } = useOne<TCommonResponse<TLMSProgramDetail>>({
    dataProviderName: 'lms',
    resource: 'programs',
    id,
    queryOptions: {
      onSuccess(data) {
        form.setFieldsValue({
          status: data.data?.data?.status,
          participants: [],
        })
      },
    },
  })

  const programStatus = Form.useWatch('status', form)
  const isValueChanged = React.useMemo(() => {
    return [
      programStatus !== data?.data?.data?.status,
      updatedParticipants.size > 0,
    ].some(Boolean)
  }, [data?.data?.data?.status, programStatus, updatedParticipants.size])

  useWarnIfUnsavedChanges(
    isValueChanged,
    'Anda memiliki perubahan yang belum disimpan. Apakah Anda yakin ingin meninggalkan halaman ini?',
  )

  const handleSave = (row: ParticipantItem) => {
    const participants = new Map(updatedParticipants)
    participants.set(row.userId, { ...participants.get(row.userId), ...row })
    setUpdatedParticipants(participants)
  }

  const handleSuccessSave = (
    data: CreateResponse<{
      message: string
      failedParticipants: {
        userId: string
        name: string
        role: string
      }[]
    }>,
  ) => {
    if (data.data.failedParticipants.length) {
      notification.open?.({
        description: `Terdapat ${data.data.failedParticipants.length} partisipan yang gagal tersimpan`,
        type: 'error',
        message: `User ID Partisipan yang gagal disimpan: \n${data.data.failedParticipants.map(({ userId }) => userId).join(',')}`,
      })
    }
    setShowModal(false)
    form.resetFields()
    setUpdatedParticipants(new Map())
    refetchProgram()
    invalidate({
      dataProviderName: 'lms',
      resource: `programs/${id}/participants`,
      invalidates: ['list'],
    })
  }

  const renderChangedInfo = (
    data: {
      initial: string
      final: string
      label: string
    }[],
  ) => {
    const isChanged = data.some(({ final, initial }) => final !== initial)
    if (!isChanged)
      return (
        <div className="px-4">
          <div className="font-bold text-[16px] -ml-4">
            Perubahan Informasi Program
          </div>
          <Empty
            description="Tidak ada perubahan informasi program"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            className="!mt-4"
          />
        </div>
      )
    return (
      <Descriptions
        size="small"
        column={2}
        title="Perubahan Informasi Program"
        bordered
        className="!mb-4"
      >
        {data.map(({ initial, final, label }) => {
          if (final === initial) {
            return null
          }
          return (
            <React.Fragment key={label}>
              <Descriptions.Item label={`${label} sebelum`}>
                {initial}
              </Descriptions.Item>
              <Descriptions.Item label={`${label} sesudah`}>
                {final}
              </Descriptions.Item>
            </React.Fragment>
          )
        })}
      </Descriptions>
    )
  }

  return (
    <Edit
      isLoading={isProgramLoading}
      title="Atur Program & Peserta"
      headerButtons={() => (
        <Button
          icon={<Icons.ProfileOutlined />}
          onClick={() => {
            setShowProgramLocalDrawer(true)
          }}
        >
          Ubah Program Lokal
        </Button>
      )}
      saveButtonProps={{
        children: 'Konfirmasi Perubahan',
        type: 'primary',
        onClick: async () => {
          setShowModal(true)
        },
        disabled: !isValueChanged,
        icon: <Icons.CheckOutlined />,
      }}
      footerButtons={({ defaultButtons }) => (
        <Space>
          <Button
            onClick={() => {
              if (window.history.length <= 2) list('alter-program-participants')
              goBack()
            }}
          >
            Kembali
          </Button>
          {defaultButtons}
        </Space>
      )}
    >
      <Head>
        <title>LMS Admin | Alter Program & Participants</title>
      </Head>
      <Form form={form} layout="vertical" onFinish={() => {}} size="small">
        <Divider orientation="left" className="!mt-0">
          Ubah Informasi Program
        </Divider>
        <Descriptions
          bordered
          column={1}
          labelStyle={{ width: 150 }}
          size="small"
        >
          <Descriptions.Item label="ID" key={'programId'}>
            {data?.data?.data?.id || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Nama" key={'programName'}>
            {data?.data?.data?.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Periode">
            {data?.data?.data ? (
              <>
                {dayjs(data?.data?.data?.startDate).format('DD MMMM YYYY')}{' '}
                &mdash;{' '}
                {dayjs(data?.data?.data?.endDate).format('DD MMMM YYYY')}
              </>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Status" key={'status'}>
            <Form.Item
              name="status"
              rules={[{ required: true, message: 'Status harus diisi' }]}
              initialValue={data?.data?.data?.status}
              noStyle
            >
              <Select
                className="w-[120px]"
                options={[
                  { label: 'Persiapan', value: PROGRAM_STATE.PREPARATION },
                  { label: 'Berjalan', value: PROGRAM_STATE.RUNNING },
                  { label: 'Selesai', value: PROGRAM_STATE.ENDED },
                ]}
                placeholder="Pilih status program"
              />
            </Form.Item>
          </Descriptions.Item>
        </Descriptions>
      </Form>

      <Divider orientation="left">Ubah Data Peserta</Divider>
      <UpdateParticipantsTable
        form={form}
        handleSave={handleSave}
        updatedParticipants={updatedParticipants}
      />

      <UpdateProgramLocalDrawer
        onClose={() => {
          setShowProgramLocalDrawer(false)
        }}
        visible={showProgramLocalDrawer}
      />

      <Modal
        title="Konfirmasi Perubahan"
        width={800}
        destroyOnClose
        onOk={async () => {
          try {
            const programData = form.getFieldsValue()
            const participantsData = Array.from(
              updatedParticipants.values(),
            ).map((item) => ({
              name: item.name,
              userId: item.userId,
              kapabilitas: item.kapabilitas,
            }))
            const payload = {
              programId: id,
              programStatus: programData.status,
              participants: participantsData.map(
                ({ kapabilitas: role, ...rest }) => ({ ...rest, role }),
              ),
            }
            await doSave(
              {
                values: payload,
                method: 'post',
                url: `${process.env.NEXT_PUBLIC_LMS_API_URL}/ops-tools/alter-program-participants`,
                dataProviderName: 'lms',
                errorNotification: (err) =>
                  showErrorNotification(
                    err,
                    'Terdapat gangguan saat mengubah program status dan/atau data peserta',
                  ),
                successNotification: () => ({
                  message: 'Ubah program dan/atau data peserta berhasil',
                  type: 'success',
                  description: 'Sukses',
                }),
              },
              {
                onSuccess: handleSuccessSave,
              },
            )
          } catch (error) {
            setShowModal(false)
            console.error('Error saving changes:', error)
          }
        }}
        onCancel={() => setShowModal(false)}
        visible={!!showModal}
        okText="Simpan Perubahan"
        cancelText="Batalkan"
        confirmLoading={isSaving}
        okButtonProps={{
          loading: isSaving,
          icon: <Icons.SaveOutlined />,
        }}
        centered
      >
        <Alert
          type="warning"
          showIcon
          message={
            <>
              Perubahan ini <b>akan berdampak</b> pada <b>LMS Admin</b> dan{' '}
              <b>tersinkronisasi dengan LMS RGTK</b>
            </>
          }
          className="!mb-3"
        />

        {renderChangedInfo([
          {
            label: 'Status',
            initial: capitalize(data?.data?.data?.status || '-'),
            final: capitalize(form.getFieldValue('status') || '-'),
          },
        ])}

        <Table
          rowKey="userId"
          size="small"
          dataSource={Array.from(updatedParticipants.values())}
          pagination={false}
          title={() => (
            <div className="font-bold text-[16px] -ml-2 mb-1">
              Perubahan Data Peserta
            </div>
          )}
          scroll={{ y: 220 }}
          columns={[
            {
              title: 'ID Peserta',
              dataIndex: 'userId',
              key: 'userId',
            },
            {
              title: 'Email',
              dataIndex: 'email',
              key: 'email',
            },
            {
              title: 'Nama Peserta',
              dataIndex: 'name',
              key: 'name',
            },
            {
              title: 'Kapabilitas',
              dataIndex: 'kapabilitas',
              key: 'kapabilitas',
              render: (value) => {
                return (
                  kapabilitasMapper(value) || (
                    <TextField value="Kosong" italic type="secondary" />
                  )
                )
              },
            },
          ]}
        />
      </Modal>
    </Edit>
  )
}
